import classNames from 'classnames';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Heading from '@jpp/atoms/Heading';
import CartMessage from '@jpp/molecules/Cart/CartMessage';
import CartProductCard from '@jpp/molecules/Cart/CartProductCard';
import CartPromoCode from '@jpp/molecules/Cart/CartPromoCode';
import CartSubTotal from '@jpp/molecules/Cart/CartSubTotal';
import EmptyCart from '@jpp/organisms/Cart/EmptyCart';
import UpSell from '@jpp/organisms/UpSell';
import Container from '@jpp/shared/Grid/Container';
import Flex from '@jpp/shared/Grid/Flex';
import Row from '@jpp/shared/Grid/Row';
import Section from '@jpp/shared/Grid/Section';
import { getOptions } from 'common/redux/core/selectors';
import { IReduxState } from 'common/redux/createStore';
import { getCart } from 'common/redux/rootActions';
import { ICartStoreState } from 'common/redux/wooCommerce/cart/reducer';
import { getCart as getCartFromState } from 'common/redux/wooCommerce/cart/selectors';
import { ETheme } from 'common/typings/enums';
import { isClient } from 'common/utils/shared';
import { getSubTotal } from 'common/utils/shared/product';

import './Cart.scss';

interface ICartProps {
  className?: string;
}

interface IStoreCartProps {
  cart: ICartStoreState;
  freeDeliveryThreshold?: string;
}

const ROOT_CLASSNAME = 'Cart';

const Cart: React.FunctionComponent<ICartProps> = ({ className }) => {
  const dispatch = useDispatch();
  const { cart, freeDeliveryThreshold } = useSelector(
    (state: IReduxState): IStoreCartProps => ({
      cart: getCartFromState(state),
      freeDeliveryThreshold: getOptions(state).free_delivery_threshold,
    })
  );
  const {
    items,
    totalItems,
    isCartLoaded,
    cartTotals: { subtotal, subtotal_tax, discount_total, discount_tax },
  } = cart || {};
  const shouldNotShowCart = totalItems === 0 && isCartLoaded;
  const isAnItemOutOfStock = items?.some((item) => {
    return (
      item.stock_status.stock_quantity <= 0 &&
      !item.stock_status.status.includes('backorder') &&
      item.stock_status.status?.toLocaleLowerCase() !== 'in stock'
    );
  });
  const isOnBackorder = items?.some((item) => {
    return item.stock_status.status.includes('backorder');
  });

  React.useEffect(() => {
    if (isClient()) {
      window.scrollTo(0, 0);
    }
  }, [shouldNotShowCart]);

  React.useEffect(() => {
    dispatch(getCart());
  }, []);

  return (
    <>
      {shouldNotShowCart ? (
        <EmptyCart />
      ) : (
        <Section
          className={classNames(ROOT_CLASSNAME, className)}
          theme={ETheme.TintAlpha}
        >
          <Container>
            <Row>
              <Flex>
                <Heading
                  priority={1}
                  isBold={true}
                  className={`${ROOT_CLASSNAME}__heading`}
                  children="Cart Summary"
                />

                {subtotal && freeDeliveryThreshold && (
                  <CartMessage
                    cartLineItems={cart.items}
                    subtotal={getSubTotal(
                      subtotal,
                      subtotal_tax,
                      discount_total,
                      discount_tax
                    )}
                    freeDeliveryThreshold={freeDeliveryThreshold}
                  />
                )}
              </Flex>
            </Row>
            <Row>
              <Flex colLg={6} className={`${ROOT_CLASSNAME}__card-wrapper`}>
                {items &&
                  items.map((item) => {
                    return (
                      <CartProductCard
                        key={item.key}
                        className={`${ROOT_CLASSNAME}__card`}
                        product={item}
                      />
                    );
                  })}
              </Flex>

              <Flex
                colLg={5}
                className={`${ROOT_CLASSNAME}__totals-wrapper ml-lg-auto`}
              >
                {subtotal && (
                  <CartSubTotal
                    className={`${ROOT_CLASSNAME}__totals`}
                    totalItems={cart.totalItems}
                    hasBackorderItems={isOnBackorder}
                    isCheckoutDisabled={isAnItemOutOfStock}
                  />
                )}

                <CartPromoCode className={`${ROOT_CLASSNAME}__promo-code`} />
              </Flex>
            </Row>
          </Container>
        </Section>
      )}

      <UpSell
        className={`${ROOT_CLASSNAME}__upsell`}
        theme={ETheme.TintAlpha}
        shouldHideOOSItems={true}
      />
    </>
  );
};

export default Cart;
